import React, {Suspense} from 'react'

import type {
    // DOMConversionMap,
    // DOMConversionOutput,
    // DOMExportOutput,
    EditorConfig,
    LexicalNode,
    NodeKey,
    SerializedLexicalNode,
    Spread,
} from 'lexical'
import {$applyNodeReplacement, DecoratorNode} from 'lexical'
import {AlignType} from "../../../../rich-editor/plugins/resizeable/align-selector/align-selector"

const ImageComponent = React.lazy(
    // @ts-ignore
    () => import('./component'),
)

export interface ImagePayload {
    altText     : string
    height?     : number
    key?        : NodeKey
    maxWidth?   : number
    src         : string
    width?      : number
    alignment?  : AlignType
}

/*function convertImageElement(domNode: Node): null | DOMConversionOutput {
    if (domNode instanceof HTMLImageElement) {
        const {alt: altText, src, width, height} = domNode;
        const node = $createImageNode({altText, height, src, width});
        return {node};
    }
    return null;
}*/

export type SerializedImageNode = Spread<
    {
        altText     : string
        height?     : number
        maxWidth?   : number
        src         : string
        width?      : number
        alignment   : AlignType
    },
    SerializedLexicalNode
>

export class ImageNode extends DecoratorNode<JSX.Element> {
    __src       : string
    __altText   : string
    __width     : 'inherit' | number
    __height    : 'inherit' | number
    __maxWidth? : number
    __alignment : AlignType

    static getType(): string {
        return 'image'
    }

    static clone(node: ImageNode): ImageNode {
        return new ImageNode(
            node.__src,
            node.__altText,
            node.__maxWidth,
            node.__width,
            node.__height,
            node.__key,
            node.__alignment,
        )
    }

    static importJSON(serializedNode: SerializedImageNode): ImageNode {
        const {altText, height, width, maxWidth, src, alignment} = serializedNode
        return $createImageNode({
            altText,
            height,
            maxWidth,
            src,
            width,
            alignment,
        })
    }

    // copy/paste?
    /*exportDOM(): DOMExportOutput {
        const element = document.createElement('img');
        element.setAttribute('src', this.__src);
        element.setAttribute('alt', this.__altText);
        element.setAttribute('width', this.__width.toString());
        element.setAttribute('height', this.__height.toString());
        return {element};
    }

    static importDOM(): DOMConversionMap | null {
        return {
            img: (node: Node) => ({
                conversion: convertImageElement,
                priority: 0,
            }),
        };
    }*/

    constructor(
        src         : string,
        altText     : string,
        maxWidth?   : number,
        width?      : 'inherit' | number,
        height?     : 'inherit' | number,
        key?        : NodeKey,
        alignment?  : AlignType
    ) {
        super(key);
        this.__src          = src
        this.__altText      = altText
        this.__maxWidth     = maxWidth
        this.__width        = width || 'inherit'
        this.__height       = height || 'inherit'
        this.__alignment    = alignment || AlignType.LEFT
    }

    exportJSON(): SerializedImageNode {
        return {
            altText     : this.__altText,
            height      : this.__height === 'inherit' ? 0 : this.__height,
            maxWidth    : this.__maxWidth,
            src         : this.__src,
            width       : this.__width === 'inherit' ? 0 : this.__width,
            alignment   : this.getAlignment(),
            type        : ImageNode.getType(),
            version     : 1,
        };
    }

    setWidthAndHeight(
        width: 'inherit' | number,
        height: 'inherit' | number,
    ): void {
        const writable = this.getWritable();
        writable.__width = width;
        writable.__height = height;
    }

    // View

    createDOM(config: EditorConfig): HTMLElement {
        const span = document.createElement('span');
        const theme = config.theme;
        const className = theme.image;
        if (className !== undefined) {
            span.className = className;
        }
        return span;
    }

    updateDOM(): false {
        return false
    }

    setAlignment(value: AlignType) {
        // getWritable() creates a clone of the node
        // if needed, to ensure we don't try and mutate
        // a stale version of this node.
        const self = this.getWritable()
        self.__alignment = value
    }

    getAlignment():AlignType {
        // getLatest() ensures we are getting the most
        // up-to-date value from the EditorState.
        const self = this.getLatest()
        return self.__alignment
    }

    getSrc(): string {
        return this.__src
    }

    getAltText(): string {
        return this.__altText
    }

    decorate(): JSX.Element {
        return (
            <Suspense fallback={null}>
                <ImageComponent src             = {this.__src}
                                altText         = {this.__altText}
                                width           = {this.__width}
                                height          = {this.__height}
                                maxWidth        = {this.__maxWidth}
                                alignment       = {this.__alignment}
                                nodeKey         = {this.getKey()}
                                resizable       = {true}/>
            </Suspense>
        )
    }
}

export function $createImageNode({altText, height, maxWidth, src, width, key, alignment}: ImagePayload): ImageNode {
    return $applyNodeReplacement(new ImageNode(src, altText, maxWidth, width, height, key, alignment))
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
    return node instanceof ImageNode
}
